import React, { useState, useEffect } from "react";
import { getDatabase, ref, onValue, off } from "firebase/database";
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  AlertCircle,
} from "lucide-react";

export default function Noticias() {
  const [noticias, setNoticias] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPaused, setIsPaused] = useState(false);

  // Cargar las noticias desde Firebase en tiempo real
  useEffect(() => {
    const db = getDatabase();
    const noticiasRef = ref(db, "App/Banner/Noticias");

    // Función que maneja los cambios en tiempo real
    const handleDataChange = (snapshot) => {
      if (snapshot.exists()) {
        // Convertir el objeto en un array y ordenar por fecha (si existe)
        const noticiasData = snapshot.val();
        const noticiasArray = Object.keys(noticiasData).map((key) => ({
          id: key,
          ...noticiasData[key],
        }));

        // Ordenar por fecha de creación si existe
        const sortedNoticias = noticiasArray.sort((a, b) => {
          // Si tienen fechaCreacion, ordenar por ella (más reciente primero)
          if (a.fechaCreacion && b.fechaCreacion) {
            return b.fechaCreacion - a.fechaCreacion;
          }
          return 0; // Mantener el orden original si no hay fechas
        });

        setNoticias(sortedNoticias);
      } else {
        setNoticias([]);
      }
      setLoading(false);
    };

    // Función que maneja errores
    const handleError = (err) => {
      console.error("Error al cargar noticias en tiempo real:", err);
      setError("No se pudieron cargar las noticias en tiempo real");
      setLoading(false);
    };

    // Configurar el listener en tiempo real
    onValue(noticiasRef, handleDataChange, handleError);

    // Limpiar el listener cuando el componente se desmonte
    return () => {
      off(noticiasRef);
    };
  }, []);

  // Navegar al siguiente slide
  const nextSlide = () => {
    if (noticias.length <= 1) return;
    setCurrentIndex((prevIndex) =>
      prevIndex === noticias.length - 1 ? 0 : prevIndex + 1
    );
  };

  // Navegar al slide anterior
  const prevSlide = () => {
    if (noticias.length <= 1) return;
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? noticias.length - 1 : prevIndex - 1
    );
  };

  // Seleccionar un slide específico
  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  // Pausar/reanudar la rotación de slides
  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  // Auto-rotación de slides
  useEffect(() => {
    if (noticias.length <= 1 || isPaused) return;

    const interval = setInterval(() => {
      nextSlide();
    }, 8000); // Cambiar cada 5 segundos

    return () => clearInterval(interval);
  }, [currentIndex, noticias.length, isPaused]);

  // Función para determinar si un string contiene código iframe
  const isIframe = (str) => {
    return str && typeof str === "string" && str.includes("<iframe");
  };

  // Renderizar un mensaje de carga con animación mejorada
  if (loading) {
    return (
      <div className="bg-gradient-to-r from-blue-900 to-purple-900 text-white p-4 rounded-lg shadow-md flex justify-center items-center h-52 md:h-64">
        <div className="flex flex-col items-center">
          <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin mb-2"></div>
          <div>Cargando noticias...</div>
        </div>
      </div>
    );
  }

  // Renderizar mensaje de error mejorado
  if (error) {
    return (
      <div className="bg-gradient-to-r from-red-800 to-red-600 text-white p-4 rounded-lg shadow-md flex justify-center items-center h-52 md:h-64">
        <div className="flex items-center">
          <AlertCircle size={24} className="mr-2" />
          <div>{error}</div>
        </div>
      </div>
    );
  }

  // Si no hay noticias, mostrar mensaje informativo
  if (noticias.length === 0) {
    return (
      <div className="bg-gradient-to-r from-blue-900 to-purple-900 text-white p-4 rounded-lg shadow-md flex justify-center items-center h-52 md:h-64">
        <div className="text-center">
          <p>No hay noticias disponibles en este momento</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-lg shadow-md mb-6">
      {/* Banner / Carrusel */}
      <div className="relative h-52 md:h-64">
        {noticias.map((noticia, index) => (
          <div
            key={noticia.id}
            className={`absolute inset-0 transition-all duration-500 ease-in-out ${
              index === currentIndex ? "opacity-100 z-10" : "opacity-0 z-0"
            }`}
          >
            <a
              href={noticia.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block h-full w-full relative group"
            >
              {/* Contenedor para la imagen o iframe */}
              <div className="absolute inset-0">
                {isIframe(noticia.imagen) ? (
                  // Renderizar iframe si la imagen contiene código iframe
                  <div
                    className="w-full h-full"
                    dangerouslySetInnerHTML={{ __html: noticia.imagen }}
                  />
                ) : (
                  // Renderizar imagen normal
                  <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                      backgroundImage: `url(${
                        noticia.imagen
                      })`,
                      backgroundSize: "cover",
                    }}
                  />
                )}
              </div>

              <div className="absolute inset-0 bg-black bg-opacity-40 group-hover:bg-opacity-30 transition-all duration-300"></div>

              <div className="absolute inset-0 flex flex-col justify-end p-4 md:p-6 text-white">
                <h2 className="text-xl md:text-2xl font-bold mb-2 drop-shadow-lg line-clamp-2">
                  {noticia.titulo}
                </h2>
                <p className="text-sm md:text-base mb-2 drop-shadow-md line-clamp-2">
                  {noticia.descripcion}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-sm text-blue-200">
                    <ExternalLink size={16} className="mr-1" />
                    {/* <span className="group-hover:underline">Ver más</span> */}
                  </div>
                  {noticia.fechaPublicacion && (
                    <span className="text-xs text-gray-300">
                      {/* {new Date(noticia.fechaPublicacion).toLocaleDateString()} */}
                    </span>
                  )}
                </div>
              </div>
            </a>
          </div>
        ))}
      </div>

      {/* Control de pausa */}
      {noticias.length > 1 && (
        <button
          onClick={togglePause}
          className="absolute top-3 right-3 z-20 bg-black bg-opacity-30 hover:bg-opacity-50 text-white rounded-full p-2 focus:outline-none transition-all duration-300"
          aria-label={isPaused ? "Reanudar" : "Pausar"}
        >
          {isPaused ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polygon points="5 3 19 12 5 21 5 3"></polygon>
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="6" y="4" width="4" height="16"></rect>
              <rect x="14" y="4" width="4" height="16"></rect>
            </svg>
          )}
        </button>
      )}

      {/* Botones de navegación (solo si hay más de una noticia) */}
      {noticias.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-black bg-opacity-30 hover:bg-opacity-50 text-white rounded-full p-2 focus:outline-none transition-all duration-300"
            aria-label="Noticia anterior"
          >
            <ChevronLeft size={24} />
          </button>

          <button
            onClick={nextSlide}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-black bg-opacity-30 hover:bg-opacity-50 text-white rounded-full p-2 focus:outline-none transition-all duration-300"
            aria-label="Siguiente noticia"
          >
            <ChevronRight size={24} />
          </button>

          {/* Indicadores de página */}
          <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
            {noticias.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`h-2 w-2 md:h-3 md:w-3 rounded-full focus:outline-none transition-all duration-300 ${
                  index === currentIndex
                    ? "bg-white"
                    : "bg-white bg-opacity-50 hover:bg-opacity-75"
                }`}
                aria-label={`Ir a la noticia ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
